import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Popconfirm, Radio, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import {
    MODULE_TYPE_LABELS,
    quizFullyAnswered,
    quizSections,
    totalQuizQuestions,
    formatScore,
    passMarkOf,
    attemptsLeft,
    languageLabel,
    certificateIssueText,
    englishOnlyNote,
} from '../../utils/trainingModel';

const { Text, Paragraph } = Typography;

/**
 * Learner surface for one enrolment: read the modules, answer the quiz, complete.
 *   GET  /projects/:id/training/courses/:courseId          (learner-safe: answer key stripped)
 *   GET  /projects/:id/training/modules/:moduleId/video    (SAS-signed URL)
 *   POST /projects/:id/training/enrollments/:id/complete   ({ answers } — scored server-side)
 *   POST /projects/:id/training/enrollments/:id/certificate/retry
 *
 * The API scores every quiz module of the course as ONE submission — exactly one answer per
 * question, concatenated in module order — against the course's server-enforced pass mark.
 * Below it the attempt is recorded but the training is NOT completed and no certificate
 * issues, so the outcome is read from the response's `passed` flag, never assumed from a 2xx.
 * The answer key never reaches the learner.
 */
const LearnerCourseModal = ({ open, projectId, enrollment, onClose, onCompleted }) => {
    const { request } = useApiRequest();
    const [answers, setAnswers] = useState([]);
    const [videoUrls, setVideoUrls] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [retryingCert, setRetryingCert] = useState(false);
    const [language, setLanguage] = useState('en');
    const [exhausted, setExhausted] = useState(false);

    const courseId = enrollment?.courseId;
    const detail = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/courses/${courseId}`, { query: { language } })).data.course,
        [projectId, courseId, language, request],
        { enabled: open && !!courseId },
    );
    const course = detail.data;

    useEffect(() => {
        if (open) {
            setAnswers([]);
            setVideoUrls({});
            setResult(null);
            setError(null);
            setExhausted(false);
            setLanguage(enrollment?.languageChoice || 'en');
        }
    }, [open, enrollment?.id]);

    const modules = course?.modules || [];
    const sections = quizSections(modules);
    const questionCount = totalQuizQuestions(modules);
    const passedNow = result?.passed === true;
    const failedAttempt = result?.passed === false;
    const alreadyCompleted = !!enrollment?.completedAt || passedNow;
    const attemptsRemaining =
        typeof result?.attemptsRemaining === 'number' ? result.attemptsRemaining : attemptsLeft(course?.maxAttempts, enrollment?.attemptCount);
    const lockedOut = exhausted || (questionCount > 0 && attemptsRemaining === 0 && !alreadyCompleted);
    const canSubmit = !alreadyCompleted && !failedAttempt && !lockedOut && (questionCount === 0 || quizFullyAnswered(questionCount, answers));

    const chooseLanguage = async (next) => {
        try {
            if (!alreadyCompleted) {
                await request('PATCH', `/projects/${projectId}/training/enrollments/${enrollment.id}/language`, { body: { language: next } });
            }
            setAnswers([]);
            setLanguage(next);
        } catch (err) {
            message.error(err.message || 'Could not switch language.');
        }
    };

    const loadVideo = async (moduleId) => {
        try {
            const { data } = await request('GET', `/projects/${projectId}/training/modules/${moduleId}/video`);
            setVideoUrls((prev) => ({ ...prev, [moduleId]: data.url }));
        } catch (err) {
            message.error(err.message || 'Could not load the video.');
        }
    };

    const complete = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const body = questionCount > 0 ? { answers: Array.from({ length: questionCount }, (_, i) => answers[i]) } : {};
            const { data } = await request('POST', `/projects/${projectId}/training/enrollments/${enrollment.id}/complete`, { body });
            setResult(data);
            if (data?.passed === false) message.warning('Not passed - your attempt was recorded. You can try again.');
            else message.success('Training completed.');
            onCompleted();
        } catch (err) {
            if (err.status === 409 && err.details?.attemptsRemaining === 0) setExhausted(true);
            else setError(err.message || 'Could not record your completion.');
        } finally {
            setSubmitting(false);
        }
    };

    const retryCertificate = async () => {
        setRetryingCert(true);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/enrollments/${enrollment.id}/certificate/retry`);
            setResult((prev) => ({ ...(prev || { passed: true }), ...data }));
            if (data.certificateStatus === 'issued') message.success('Certificate issued.');
            else message.warning(data.certificateWarning || 'The certificate still could not be generated.');
            onCompleted();
        } catch (err) {
            message.error(err.message || 'Could not retry the certificate.');
        } finally {
            setRetryingCert(false);
        }
    };

    const setAnswer = (index, value) =>
        setAnswers((prev) => {
            const next = prev.slice();
            next[index] = value;
            return next;
        });

    const renderModule = (m) => {
        const section = sections.find((s) => s.module.id === m.id);
        return (
            <div key={m.id} data-testid={`learner-module-${m.id}`} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <Space wrap style={{ marginBottom: 6 }}>
                    <Text type="secondary">#{m.ordinal}</Text>
                    <Tag>{MODULE_TYPE_LABELS[m.moduleType] || m.moduleType}</Tag>
                    <Text strong>{m.title}</Text>
                    {typeof m.durationMinutes === 'number' && <Text type="secondary">{m.durationMinutes} min</Text>}
                </Space>
                {m.moduleType === 'reading' && <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{m.contentJson?.text}</Paragraph>}
                {m.moduleType === 'video' &&
                    (m.contentBlobUrl ? (
                        videoUrls[m.id] ? (
                            <video src={videoUrls[m.id]} controls style={{ width: '100%', borderRadius: 8 }} />
                        ) : (
                            <Button onClick={() => loadVideo(m.id)}>Load video</Button>
                        )
                    ) : (
                        <Text type="secondary">The video for this module has not been uploaded yet.</Text>
                    ))}
                {m.moduleType === 'live_session' && (
                    <Text type="secondary">A live session - join it from Meetings at the scheduled time.</Text>
                )}
                {m.moduleType === 'quiz' && section && (
                    <div>
                        {(m.contentJson?.questions || []).map((q, qi) => {
                            const index = section.offset + qi;
                            return (
                                <div key={qi} style={{ marginBottom: 12 }}>
                                    <Text strong>{qi + 1}. {q.prompt}</Text>
                                    <Radio.Group
                                        disabled={alreadyCompleted || failedAttempt}
                                        style={{ display: 'block', marginTop: 4 }}
                                        value={answers[index]}
                                        onChange={(e) => setAnswer(index, e.target.value)}
                                    >
                                        {q.options.map((o, oi) => (
                                            <div key={oi}><Radio value={oi}>{o}</Radio></div>
                                        ))}
                                    </Radio.Group>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const finalEnrollment = result?.enrollment || enrollment;
    const passMark = passMarkOf(result, finalEnrollment) ?? (typeof course?.passMarkPercent === 'number' ? course.passMarkPercent : null);
    const certificateFailed = passedNow && result?.certificateStatus === 'failed';
    const retry = () => {
        setResult(null);
        setAnswers([]);
        setError(null);
    };

    return (
        <Modal
            open={open}
            width={760}
            title={course ? `${course.title} (v${course.version})` : 'Training'}
            onCancel={onClose}
            destroyOnClose
            footer={
                alreadyCompleted ? (
                    <Button onClick={onClose}>Close</Button>
                ) : failedAttempt ? (
                    <Space>
                        <Button onClick={onClose}>Close</Button>
                        <Button type="primary" disabled={result?.attemptsRemaining === 0} onClick={retry}>Try again</Button>
                    </Space>
                ) : (
                    <Space>
                        <Button onClick={onClose}>Close</Button>
                        <Popconfirm
                            title="Submit your attempt?"
                            description={`Your attempt is recorded as audit evidence.${passMark !== null && questionCount > 0 ? ` You need ${passMark}% or more for the training to count as completed.` : ''}`}
                            okText="Submit"
                            disabled={!canSubmit}
                            onConfirm={complete}
                        >
                            <Button type="primary" disabled={!canSubmit} loading={submitting}>
                                {questionCount > 0 ? 'Submit quiz' : 'Mark training complete'}
                            </Button>
                        </Popconfirm>
                    </Space>
                )
            }
        >
            {detail.status === 'loading' && !course && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
            {detail.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load this training"
                    description={detail.error?.message}
                    action={
                        language !== 'en' ? (
                            <Button size="small" onClick={() => setLanguage('en')}>Use English</Button>
                        ) : (
                            <Button size="small" onClick={detail.reload}>Retry</Button>
                        )
                    }
                />
            )}
            {course && (
                <div>
                    {course.description && <Paragraph>{course.description}</Paragraph>}
                    {(course.selectableLanguages || []).length > 1 && (
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ marginRight: 8 }}>Language</Text>
                            <Select
                                aria-label="Course language"
                                style={{ minWidth: 160 }}
                                value={course.servedLanguage || language}
                                onChange={chooseLanguage}
                                options={course.selectableLanguages.filter((l) => l === 'en' || !course.languageCoverage || course.languageCoverage.some((c) => c.language === l && c.selectable)).map((l) => ({ value: l, label: languageLabel(l) }))}
                            />
                        </div>
                    )}
                    {(course.languageCoverage || []).find((c) => c.language === (course.servedLanguage || language) && c.selectable) && englishOnlyNote((course.languageCoverage.find((c) => c.language === (course.servedLanguage || language)) || {}).englishOnlyModuleTypes) && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            {englishOnlyNote(course.languageCoverage.find((c) => c.language === (course.servedLanguage || language)).englishOnlyModuleTypes)}
                        </Text>
                    )}
                    {questionCount > 0 && passMark !== null && !alreadyCompleted && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            Pass mark: {passMark}%{attemptsRemaining !== null ? ` · attempts remaining: ${attemptsRemaining}` : ''}
                        </Text>
                    )}
                    {lockedOut && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message="No attempts left"
                            description="You have used every attempt for this course. Ask your Lead Advisor to reset your attempts."
                        />
                    )}
                    {modules.length === 0 && <Alert type="warning" showIcon message="This course has no modules." />}
                    {modules.map(renderModule)}
                    {alreadyCompleted && (
                        <Alert
                            type="success"
                            showIcon
                            message="Training completed"
                            description={finalEnrollment?.score ? `Your result: ${formatScore(finalEnrollment.score)}.` : 'Your completion has been recorded.'}
                        />
                    )}
                    {certificateFailed && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 8 }}
                            message="Your certificate could not be generated"
                            description={certificateIssueText(result.certificateReason)}
                            action={<Button size="small" loading={retryingCert} onClick={retryCertificate}>Retry certificate</Button>}
                        />
                    )}
                    {failedAttempt && (
                        <Alert
                            type="error"
                            showIcon
                            message="Not passed - try again"
                            description={`${formatScore(finalEnrollment?.score) !== '-' ? `Your result: ${formatScore(finalEnrollment?.score)}. ` : ''}${passMark !== null ? ` The pass mark is ${passMark}%.` : ''} This attempt was recorded; the training is not yet completed and no certificate has been issued.${typeof result?.attemptsRemaining === 'number' ? ` Attempts remaining: ${result.attemptsRemaining}.` : ''}`}
                        />
                    )}
                    {!alreadyCompleted && !failedAttempt && questionCount > 0 && !canSubmit && (
                        <Text type="secondary">Answer every question to submit.</Text>
                    )}
                    {error && <div><Text type="danger" role="alert">{error}</Text></div>}
                </div>
            )}
        </Modal>
    );
};

export default LearnerCourseModal;
